import type { ZodType } from "zod";

import {
  FoodTruckStopListResponseSchema,
  FoodTruckStopStatusSchema,
  FoodTruckStopTransitionResponseSchema,
  OfflineQueuedResultSchema,
  RushInsightsSchema,
} from "@/lib/api/schemas/operations";
import type { FoodTruckStop } from "@/lib/api/schemas/operations";
import { ApiResponseError, assertOk, parseJson } from "@/lib/api/response";
import { z } from "zod";

type ApiFetchFn = typeof import("@/lib/api/requests")["apiFetch"];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === "function") {
    try {
      const mockedModule = jestGlobal.requireMock("@/lib/api/requests") as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === "function") {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import("@/lib/api/requests");
  return module.apiFetch;
};

type StopStatus = z.infer<typeof FoodTruckStopStatusSchema>;

type ListStopsParams = {
  status?: StopStatus[];
  from?: string;
  to?: string;
  truckId?: string;
  signal?: AbortSignal;
};

const buildQuery = (params?: ListStopsParams): string => {
  if (!params) {
    return "";
  }

  const search = new URLSearchParams();

  if (params.status?.length) {
    const uniqueStatuses = Array.from(new Set(params.status));
    if (uniqueStatuses.length > 0) {
      search.set("status", uniqueStatuses.join(","));
    }
  }

  if (typeof params.truckId === "string" && params.truckId.length > 0) {
    search.set("truckId", params.truckId);
  }

  if (typeof params.from === "string" && params.from.length > 0) {
    search.set("from", params.from);
  }

  if (typeof params.to === "string" && params.to.length > 0) {
    search.set("to", params.to);
  }

  const query = search.toString();
  return query ? `?${query}` : "";
};

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

const hasHeader = (headers: Record<string, string>, name: string): boolean =>
  Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());

const serialiseBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }
  if (typeof body === "string") {
    return body;
  }
  if (body instanceof URLSearchParams) {
    return body;
  }
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    return body;
  }
  if (typeof Blob !== "undefined" && body instanceof Blob) {
    return body;
  }
  return JSON.stringify(body);
};

const requestJson = async <T>(
  path: string,
  schema: ZodType<T>,
  { method = "GET", body, signal, headers = {} }: RequestOptions = {},
): Promise<T> => {
  const apiFetch = await resolveApiFetch();
  const requestHeaders: Record<string, string> = {
    Accept: "application/json",
    ...headers,
  };

  const serialisedBody = serialiseBody(body);
  if (serialisedBody !== undefined && typeof serialisedBody === "string" && !hasHeader(requestHeaders, "content-type")) {
    requestHeaders["Content-Type"] = "application/json";
  }

  const response = await apiFetch(path, {
    method,
    body: serialisedBody,
    signal,
    headers: requestHeaders,
  });

  await assertOk(response);
  return parseJson(response, schema);
};

export const fetchFoodTruckStops = async (params?: ListStopsParams): Promise<FoodTruckStop[]> => {
  const payload = await requestJson(`/api/ops/food-trucks/stops${buildQuery(params)}`, FoodTruckStopListResponseSchema, {
    signal: params?.signal,
  });
  return payload.stops;
};

export const transitionFoodTruckStopStatus = async (
  stopId: string,
  status: StopStatus,
): Promise<{ queued: boolean; stop: FoodTruckStop | null; queueId?: string | null }> => {
  const body = {
    status,
    clientRequestId: `truck-status-${stopId}-${Date.now()}`,
  };

  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/ops/food-trucks/stops/${stopId}/status`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");

  if (response.status === 202) {
    if (!isJson) {
      throw new ApiResponseError("Expected JSON response for queued transition", response.status);
    }
    const payload = await parseJson(response, OfflineQueuedResultSchema);
    return {
      queued: true,
      stop: null,
      queueId: payload.queueId,
    };
  }

  if (!isJson) {
    const errorText = await response.text().catch(() => "Failed to update stop status");
    throw new ApiResponseError(errorText, response.status);
  }

  await assertOk(response);

  const payload = await parseJson(response, FoodTruckStopTransitionResponseSchema);
  if (!payload.stop) {
    if (import.meta.env.DEV) {
      console.warn("[operations] transition response missing stop payload", { stopId, payload });
    }
    return {
      queued: false,
      stop: null,
      queueId: null,
    };
  }
  return {
    queued: false,
    stop: payload.stop,
  };
};

export type { FoodTruckStop, StopStatus };

const RushInsightsResponseSchema = z.object({
  ok: z.literal(true),
  insights: RushInsightsSchema,
});

const RushOverrideResponseSchema = z.object({
  ok: z.literal(true),
  override: z.object({
    source: z.literal('manual'),
    level: z.enum(['nominal', 'watch', 'critical']),
    expiresAt: z.number(),
    reason: z.string().nullable().optional(),
    updatedBy: z.string().nullable().optional(),
  }),
});

const RushOverrideClearResponseSchema = z.object({
  ok: z.literal(true),
});

type RushInsightsRequestParams = {
  locationId?: string | null;
};

export const fetchRushInsights = async (params?: RushInsightsRequestParams) => {
  const search = new URLSearchParams();
  if (params?.locationId && params.locationId.trim().length > 0) {
    search.set('locationId', params.locationId);
  }
  const path =
    search.size > 0 ? `/api/ops/rush-insights?${search.toString()}` : '/api/ops/rush-insights';
  const payload = await requestJson(path, RushInsightsResponseSchema);
  return payload.insights;
};

export const setRushOverride = async (params: {
  level: 'watch' | 'critical';
  durationMinutes?: number;
  reason?: string;
  locationId?: string;
}) => {
  const payload = await requestJson('/api/ops/rush-override', RushOverrideResponseSchema, {
    method: 'POST',
    body: {
      level: params.level,
      durationMinutes: params.durationMinutes,
      reason: params.reason,
      locationId: params.locationId,
    },
  });
  return payload.override;
};

export const clearRushOverride = async (locationId?: string) => {
  await requestJson('/api/ops/rush-override', RushOverrideClearResponseSchema, {
    method: 'DELETE',
    body: locationId ? { locationId } : undefined,
  });
};
